import { skipToken, useQuery } from '@tanstack/react-query';

import { Workspaces } from '@air/api';
import { type Airror } from '@air/errors';
import { callAirApi } from '@air/utils-api';

import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { canManageSubscription } from '~/utils/permissions/workspacePermissions';

export type UpdateCreditPacksPreviewResponse = Exclude<
  Awaited<ReturnType<typeof Workspaces.Subscriptions.Invoices.preview>>,
  Airror
>;

export const UPDATE_CREDIT_PACKS_PREVIEW = 'UPDATE_CREDIT_PACKS_PREVIEW';

export const getUpdateCreditPacksPreviewKey = ({
  workspaceId,
  creditPackQuantity,
}: {
  workspaceId: string | undefined;
  creditPackQuantity: number | undefined;
}) => {
  return [
    UPDATE_CREDIT_PACKS_PREVIEW,
    { workspaceId, ...(creditPackQuantity !== undefined && { creditPackQuantity }) },
  ];
};

export const useUpdateCreditPacksPreview = ({ creditPackQuantity }: { creditPackQuantity: number | undefined }) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();

  const isEnabled = !!workspaceId && creditPackQuantity !== undefined && canManageSubscription(workspacePermissions);

  return useQuery({
    queryKey: getUpdateCreditPacksPreviewKey({ workspaceId, creditPackQuantity }),
    queryFn: isEnabled
      ? () => callAirApi(() => Workspaces.Subscriptions.Invoices.preview({ workspaceId, creditPackQuantity }))
      : skipToken,
  });
};
